import { baseApi } from "./baseApi";


export const staffApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // fetch all employees
    getAllStaff: builder.query<any, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 10 }) => ({
        url: `/employees/all-employees?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["staff"],
    }),

    getSingleUser: builder.query({
      query: (id: string | number) => ({
        url: `/employees/staffs/${id}`,
        method: "GET",
      }),
      providesTags: ["users"],
      transformResponse: (response: any) => response,
    }),


    // staff statistics
    getStaffStatistics: builder.query<any, void>({
      query: () => ({
        url: "/staffs/statistics",
        method: "GET",
      }),
      providesTags: ["users"],
    }),

    // fetch all invitations
    getAllInvites: builder.query<any, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 10 }) => ({
        url: `/invites/all-invite?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["invites"],
    }),

    // accept staff invite
    acceptStaffInvite: builder.mutation<
      any,
      { id: string; password: string; password_confirmation: string }
    >({
      query: ({ id, password, password_confirmation }) => ({
        url: `/invite/${id}/accept`,
        method: "POST",
        body: { password, password_confirmation },
      }),
    }),

    // create staff invitation
    createStaffInvitation: builder.mutation<
      any,
      {
        first_name: string;
        last_name: string;
        email: string;
        phone_number: string;
        gender: string;
        resumption_date: string;
        branch_id: string;
        company_id: string;
        role_id: string;
      }
    >({
      query: (body) => ({
        url: "/invites/create-invitation",
        method: "POST",
        body,
      }),
      invalidatesTags: ["planLimit", "staff", "invites"],
    }),

    // fetch companies
    getCompanies: builder.query<any, void>({
      query: () => ({
        url: "/company/get-company",
        method: "GET",
      }),
      providesTags: ["companies"],
    }),

    // update staff role
    updateStaffRole: builder.mutation<any, { staffId: string; role_id: string }>({
      query: ({ staffId, role_id }) => ({
        url: `/employees/staffs/${staffId}`,
        method: "PUT",
        body: { role_id },
      }),
      invalidatesTags: ["planLimit", "staff"],
    }),

    // fetch roles
    getRoles: builder.query<any, void>({
      query: () => ({
        url: "/role",
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data?.data?.data ?? [],
      providesTags: ["roles"],
    }),


    // fetch permissions by role
    getRolePermissions: builder.query<any, string>({
      query: (roleId) => ({
        url: `/role/${roleId}/permissions`,
        method: "GET",
      }),
      transformResponse: (response: any) =>
        response?.data?.data ?? response?.data ?? [],
      providesTags: ["roles"],
    }),



    // Fetch staff members by company
    getStaffByCompany: builder.query<any, void>({
      query: () => ({
        url: `/employees/staff-members/by-company`,
        method: "GET",
      }),

      providesTags: ["staff"],
    }),

    // Delete staff integrated with company endpoint
    deleteStaff: builder.mutation<any, { staffId: string }>({
      async queryFn({ staffId }, _queryApi, _extraOptions, baseQuery) {
        // fetch all staff members by company
        const staffRes = await baseQuery({
          url: `/employees/staff-members/by-company`,
          method: "GET",
        });

        if (staffRes.error) {
          return { error: staffRes.error };
        }

        // normalize and find staff record
        const staffList = (staffRes.data as any)?.data?.staff_members || staffRes.data || [];
        const matchedStaff = staffList.find(
          (s: any) => s.id === staffId || s.user_id === staffId || s.user?.id === staffId
        );

        if (!matchedStaff?.id) {
          return { error: { status: 404, data: "Staff not found in company list" } };
        }

        // second  Delete staff record
        const deleteRes = await baseQuery({
          url: `/employees/staffs/${matchedStaff.id}`,
          method: "DELETE",
        });

        if (deleteRes.error) {
          return { error: deleteRes.error };
        }

        return { data: deleteRes.data };
      },
      invalidatesTags: ["planLimit", "staff"],
    }),

    // deactivate staff (requires reason)
    deactivateStaff: builder.mutation<any, { staffId: string; reason: string }>({
      query: ({ staffId, reason }) => ({
        url: `/employees/staff/${staffId}/deactivate`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["planLimit", "staff"],
    }),

    // reactivate staff
    reactivateStaff: builder.mutation<any, { staffId: string }>({
      query: ({ staffId }) => ({
        url: `/employees/staff/${staffId}/reactivate`,
        method: "POST",
      }),
      invalidatesTags: ["planLimit", "staff"],
    }),

    // delete invitation
    deleteInvite: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/invites/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // update invitation
    updateInvite: builder.mutation<
      any,
      {
        id: string;
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        gender?: string;
        resumption_date?: string;
        branch_id?: string;
        company_id?: string;
        role_id?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/invites/update/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // resend invitation
    resendInvite: builder.mutation<any, string>({
      query: (id) => ({
        url: `/invites/invites/${id}/resend`,
        method: "POST",
      }),
      invalidatesTags: ["planLimit", "invites"],
    }),



    // update staff user profile including profile image
    updateStaffUser: builder.mutation<
      any,
      {
        userId: string | number;
        body: FormData;
      }
    >({
      query: ({ userId, body }) => ({
        url: `/employees/users/${userId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["planLimit", "staff", "users"],
    }),


    // download bulk upload template
    downloadInvitationTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "/invites/download-template",
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    // bulk upload staff (CSV or XLSX)
    bulkUploadStaff: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: `/invites/bulk-upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["planLimit", "staff", "invites"],
    }),
  }),
});

export const {
  useGetAllStaffQuery,
  useGetStaffStatisticsQuery,
  useGetAllInvitesQuery,
  useCreateStaffInvitationMutation,
  useGetRolesQuery,
  useGetCompaniesQuery,
  useUpdateStaffRoleMutation,
  useDeactivateStaffMutation,
  useReactivateStaffMutation,
  useAcceptStaffInviteMutation,
  useDeleteInviteMutation,
  useUpdateInviteMutation,
  useResendInviteMutation,
  useDeleteStaffMutation,
  useLazyDownloadInvitationTemplateQuery,
  useBulkUploadStaffMutation,
  useGetStaffByCompanyQuery,

  useGetSingleUserQuery,
  useUpdateStaffUserMutation,
  useGetRolePermissionsQuery,


} = staffApi;
